import {QueryReturnValue} from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import {FetchBaseQueryError} from '@reduxjs/toolkit/query';

import {userJobDispatcher} from 'app/jobs/userJobDispatcher';
import {sweetAPI} from 'app/store/sweet-api';
import {showLoadingBar, showMessages} from 'app/store/sweet-api/utils';
import {OPENAI_SUGGESTIONS_API_URL} from 'app/urls';
import {gt} from 'app/utils/i18n';

import {SuggestionItem} from './types';

import type {UserJobSerializedType} from 'sweet-types';

const OPENAI_RTK_TAGS = {
  TAGS: 'openai',
};

export const extendedAPI = sweetAPI
  .enhanceEndpoints({addTagTypes: [OPENAI_RTK_TAGS.TAGS]})
  .injectEndpoints({
    endpoints: (build) => ({
      getSuggestion: build.query<
        {results: Array<SuggestionItem>; url: string},
        string
      >({
        query: (url) => ({url}),
        providesTags: (response) =>
          response != null
            ? [{type: OPENAI_RTK_TAGS.TAGS, id: response.url}]
            : [],

        onQueryStarted: showMessages({
          onError: () => gt('Could not suggest document titles.'),
        }),
      }),
      createSuggestions: build.mutation<
        UserJobSerializedType & {status: 'completed'},
        {
          prompt: string;
          content_types: Array<string>;
        }
      >({
        queryFn: (
          body,
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<
            UserJobSerializedType & {status: 'completed'},
            FetchBaseQueryError,
            any
          >
        > => {
          return api
            .dispatch(
              userJobDispatcher(
                (
                  baseQuery({
                    url: OPENAI_SUGGESTIONS_API_URL,
                    method: 'POST',
                    body,
                  }) as Promise<
                    QueryReturnValue<
                      {job: UserJobSerializedType},
                      FetchBaseQueryError,
                      any
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .job.then(
              (job) => ({data: job}),
              (error) => ({error})
            );
        },
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: {
              fallback: () => gt('There was an error writing the procedure.'),
              byStatus: {
                400: (response) => response?.non_field_errors?.[0],
                429: () =>
                  gt(
                    'Daily limit for for AI requests exceeded.' +
                      ' Contact support@sweetprocess.com if you want to continue using this feature.'
                  ),
              },
            },
          })(args, api);
        },
        invalidatesTags: () => [{type: OPENAI_RTK_TAGS.TAGS, id: 'list'}],
      }),
    }),
  });

export const {
  useCreateSuggestionsMutation: useCreateSuggestions,
  useGetSuggestionQuery: useSuggestion,
} = extendedAPI;
